import { useEffect, useRef, useState } from 'react';

interface LumiAvatarInteractiveProps {
  className?: string;
  size?: number;
}

export function LumiAvatarInteractive({ 
  className = '', 
  size = 48 
}: LumiAvatarInteractiveProps) {
  const avatarRef = useRef<HTMLDivElement>(null);
  const [leftPupilPosition, setLeftPupilPosition] = useState({ x: 0, y: 0 });
  const [rightPupilPosition, setRightPupilPosition] = useState({ x: 0, y: 0 });

  // Base size is 120px from original, calculate scale
  const scale = size / 120;
  
  // Exact dimensions from original (scaled)
  const eyeWidth = 42 * scale;
  const eyeHeight = 40 * scale;
  const pupilSize = 18 * scale;
  const highlightSize = 5 * scale;
  const eyeGap = 4 * scale; // gap-1 = 4px
  
  // Exact positioning from original
  const eyesTopOffset = 18 * scale; // top-[18px] from original
  const noseBottomOffset = 28 * scale; // bottom-[28px] from original
  const noseFontSize = 32 * scale; // text-[32px] from original

  // Default pupil positions (slight offset like original)
  const leftDefaultOffset = { x: -2 * scale, y: -2 * scale };
  const rightDefaultOffset = { x: 2 * scale, y: 2 * scale };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!avatarRef.current) return;

      const avatar = avatarRef.current;
      const avatarRect = avatar.getBoundingClientRect();
      const avatarCenterX = avatarRect.left + avatarRect.width / 2;
      const avatarCenterY = avatarRect.top + avatarRect.height / 2;

      // Calculate positions for both eyes
      const calculatePupilPosition = (eyeOffsetX: number, defaultOffset: { x: number; y: number }) => {
        const eyeCenterX = avatarCenterX + eyeOffsetX;
        const eyeCenterY = avatarRect.top + eyesTopOffset + (eyeHeight / 2);

        // Calculate direction from eye to mouse
        const dx = e.clientX - eyeCenterX;
        const dy = e.clientY - eyeCenterY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 1) {
          return { x: defaultOffset.x, y: defaultOffset.y };
        }

        // Calculate max movement (eye radius minus pupil radius with buffer)
        const maxMovementX = (eyeWidth - pupilSize) / 2 - 2 * scale;
        const maxMovementY = (eyeHeight - pupilSize) / 2 - 2 * scale;
        
        // Normalize and apply movement
        const moveRatio = Math.min(distance / 100, 1);
        const nx = (dx / distance) * maxMovementX * moveRatio;
        const ny = (dy / distance) * maxMovementY * moveRatio;

        return { 
          x: nx + defaultOffset.x, 
          y: ny + defaultOffset.y 
        };
      };

      // Calculate eye horizontal positions
      const leftEyeX = -(eyeWidth + eyeGap) / 2;
      const rightEyeX = (eyeWidth + eyeGap) / 2 - eyeWidth;

      setLeftPupilPosition(calculatePupilPosition(leftEyeX + eyeWidth/2, leftDefaultOffset));
      setRightPupilPosition(calculatePupilPosition(rightEyeX + eyeWidth/2, rightDefaultOffset));
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [scale, eyeWidth, eyeHeight, pupilSize, eyeGap, eyesTopOffset, leftDefaultOffset.x, leftDefaultOffset.y, rightDefaultOffset.x, rightDefaultOffset.y]);

  return (
    <div
      ref={avatarRef}
      className={`relative ${className}`}
      style={{ 
        width: size, 
        height: size,
        background: `radial-gradient(circle at 35% 35%, #FFE55C 0%, #FFD700 50%, #FFC700 100%)`,
        borderRadius: '50%',
        overflow: 'hidden',
        boxShadow: 'none',
        position: 'relative'
      }}
    >
      {/* Glossy overlay for shiny effect */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '5%',
          left: '10%',
          width: '40%',
          height: '30%',
          background: 'radial-gradient(ellipse, rgba(255, 255, 255, 0.4) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(2px)'
        }}
      />

      {/* Eyes Container - positioned at exact top offset */}
      <div
        className="absolute flex gap-1 items-center justify-center"
        style={{
          top: eyesTopOffset,
          left: '50%',
          transform: 'translateX(-50%)',
          gap: `${eyeGap}px`
        }}
      >
        {/* Left Eye */}
        <div
          className="relative bg-white flex items-center justify-center"
          style={{
            width: eyeWidth,
            height: eyeHeight,
            borderRadius: '50% 50% 45% 45%', // Slightly oval like original
          }}
        >
          {/* Left Pupil */}
          <div
            className="absolute bg-black rounded-full"
            style={{
              width: pupilSize,
              height: pupilSize,
              transform: `translate(${leftPupilPosition.x}px, ${leftPupilPosition.y}px)`,
              background: 'radial-gradient(circle at 30% 30%, #333, #000)'
            }}
          />
          {/* White highlight - moves with pupil */}
          <div
            className="absolute bg-white rounded-full pointer-events-none"
            style={{
              width: highlightSize,
              height: highlightSize,
              transform: `translate(${leftPupilPosition.x - 2 * scale}px, ${leftPupilPosition.y - 2 * scale}px)`,
            }}
          />
          {/* Secondary smaller highlight for sparkle */}
          <div
            className="absolute bg-white rounded-full pointer-events-none opacity-60"
            style={{
              width: highlightSize * 0.5,
              height: highlightSize * 0.5,
              transform: `translate(${leftPupilPosition.x + 3 * scale}px, ${leftPupilPosition.y + 3 * scale}px)`
            }}
          />
        </div>

        {/* Right Eye */}
        <div
          className="relative bg-white flex items-center justify-center"
          style={{
            width: eyeWidth,
            height: eyeHeight,
            borderRadius: '50% 50% 45% 45%', // Slightly oval like original
          }}
        >
          {/* Right Pupil */}
          <div
            className="absolute bg-black rounded-full"
            style={{
              width: pupilSize,
              height: pupilSize,
              transform: `translate(${rightPupilPosition.x}px, ${rightPupilPosition.y}px)`,
              background: 'radial-gradient(circle at 30% 30%, #333, #000)'
            }}
          />
          {/* White highlight - moves with pupil */}
          <div
            className="absolute bg-white rounded-full pointer-events-none"
            style={{
              width: highlightSize,
              height: highlightSize,
              transform: `translate(${rightPupilPosition.x - 2 * scale}px, ${rightPupilPosition.y - 2 * scale}px)`,
            }}
          />
          {/* Secondary smaller highlight for sparkle */}
          <div
            className="absolute bg-white rounded-full pointer-events-none opacity-60"
            style={{
              width: highlightSize * 0.5,
              height: highlightSize * 0.5,
              transform: `translate(${rightPupilPosition.x + 3 * scale}px, ${rightPupilPosition.y + 3 * scale}px)`
            }}
          />
        </div>
      </div>

      {/* L-shaped Nose - using text like original */}
      <div
        className="absolute text-white select-none lumi-avatar-nose"
        style={{
          bottom: noseBottomOffset,
          left: '50%',
          transform: 'translateX(-50%)',
          ['--lumi-avatar-nose-fs' as any]: `${noseFontSize}px`
        }}
      >
        L
      </div>
    </div>
  );
}